import { S3Client, PutObjectCommand } from '@aws-sdk/client-s3';
import type { DashboardUploaderS3Cfg } from '@/lib/types/Upload';

/**
 * Lightweight S3 uploader for small files (text tracks, poster images).
 * Uses single PutObject — no multipart overhead.
 *
 * Client caching mirrors S3MultipartSigner.getClient() — see that file
 * for configuration rationale (acceleration, clock drift, checksum settings).
 */
export class S3FileUploader {
  private static cachedClient: S3Client | null = null;
  private static cachedCacheKey: string = '';

  private static getClient(cfg: DashboardUploaderS3Cfg): S3Client {
    const cacheKey = `${cfg.accessKey}:${cfg.expirationTS}`;
    if (this.cachedClient && this.cachedCacheKey === cacheKey) {
      return this.cachedClient;
    }
    this.cachedClient = new S3Client({
      region: cfg.region,
      credentials: {
        accessKeyId: cfg.accessKey,
        secretAccessKey: cfg.secretKey,
        sessionToken: cfg.sessionToken,
      },
      useAccelerateEndpoint: true,
      systemClockOffset: cfg.clockDriftSecs * 1000,
      requestChecksumCalculation: 'WHEN_REQUIRED',
      responseChecksumValidation: 'WHEN_REQUIRED',
    });
    this.cachedCacheKey = cacheKey;
    return this.cachedClient;
  }

  /**
   * Upload a small file directly to S3 (single PUT, no multipart).
   * Ideal for text tracks (<100KB), poster images (<10MB).
   */
  static async putObject(
    cfg: DashboardUploaderS3Cfg,
    key: string,
    file: File,
    contentType: string
  ): Promise<void> {
    const client = this.getClient(cfg);
    const arrayBuffer = await file.arrayBuffer();

    await client.send(
      new PutObjectCommand({
        Bucket: cfg.bucket,
        Key: key,
        Body: new Uint8Array(arrayBuffer),
        ContentType: contentType,
        ServerSideEncryption: 'AES256',
        ACL: 'private',
      })
    );
  }
}
